/**
 * Pure sync engine — orchestrates entity-level sync logic.
 *
 * Every entity in the schema registry (folders, settings, quizProgress,
 * annotations, groupMemberships, userInfo, cards, deckProperties) is
 * iterated via the generic dispatch maps in `entity-registry.ts`. The
 * previous bespoke `deckEntity` orchestration was retired in Phase 5.3b
 * along with the `card-push` / `deck-push` / `deck-meta-push` /
 * `deck-delete` wire types — the generic `cards-push` /
 * `deckProperties-push` path now owns those mutations on both ends.
 *
 * No React, no WebSocket, no side effects. Fully unit-testable.
 */

import type { SyncClientMessage, SyncServerMessage } from '../schema/sync-types';
import { getSyncClientCapabilities } from './client-capabilities';
import { genericEntities, recordEntities } from './entity-registry';
import type { SyncDiffResult, SyncSnapshot } from './sync-engine-types';

// Re-export types so existing consumers don't need to change imports
export type { SyncDiffResult, SyncSnapshot } from './sync-engine-types';

const EPOCH = new Date(0).toISOString();

// ---------------------------------------------------------------------------
// buildSyncInit
// ---------------------------------------------------------------------------

/**
 * Build the sync-init handshake message from local state.
 */
export function buildSyncInit(
  snap: SyncSnapshot,
): Extract<SyncClientMessage, { type: 'sync-init' }> {
  // Record entity timestamps (per-entry)
  const recordTimestamps: Record<string, Record<string, string>> = {};
  for (const entity of recordEntities) {
    const prefix = entity.tsKeyPrefix!;
    const entityTs: Record<string, string> = {};
    for (const [k, v] of Object.entries(snap.serverTimestamps)) {
      if (k.startsWith(prefix)) {
        entityTs[k.slice(prefix.length)] = v;
      }
    }
    recordTimestamps[entity.stateKey] = entityTs;
  }

  // Generic-entity timestamps — one entry per non-record entity in the
  // schema registry. Keyed by `tsKey` (e.g. "__folders"). Adding a new
  // entity to the schema automatically extends this map; the server
  // mirrors the same iteration to build its `clientTsMap`.
  const genericTimestamps: Record<string, string> = {};
  for (const entity of genericEntities) {
    genericTimestamps[entity.tsKey] = snap.serverTimestamps[entity.tsKey] ?? EPOCH;
  }

  return {
    type: 'sync-init',
    genericTimestamps,
    recordTimestamps,
    // Cold-deck capability is OPT-IN per platform (web sets it; native does
    // not). Only when true does the server withhold cold decks' card bodies.
    ...(getSyncClientCapabilities().coldDecks ? { supportsColdDecks: true } : {}),
  } as Extract<SyncClientMessage, { type: 'sync-init' }>;
}

// ---------------------------------------------------------------------------
// applySyncDiff
// ---------------------------------------------------------------------------

/**
 * Process a sync-diff message against local state.
 * Returns what to apply locally and what to push back to the server.
 */
export function applySyncDiff(
  diff: Extract<SyncServerMessage, { type: 'sync-diff' }>,
  snap: SyncSnapshot,
): SyncDiffResult {
  const ts = { ...snap.serverTimestamps };
  const outbound: SyncClientMessage[] = [];

  // Generic entities live under `diff.generics`, keyed by the entity's
  // `diffKey`. Records keep their own top-level fields because they
  // have parallel wire data (serverKeys, per-deck timestamps).
  const genericsBucket = (diff.generics ?? {}) as Record<string, unknown>;

  // Per-entity applySyncDiff/flushLocal still read their section via
  // `d[diffKey]`, so wrap each section back into that shape. Adding an
  // entity to the schema extends this loop automatically — no boundary
  // plumbing required.
  const buildEntityDiff = (diffKey: string): Record<string, unknown> =>
    diffKey in genericsBucket ? { [diffKey]: genericsBucket[diffKey] } : {};

  // ── Record entities (from registry — includes cards as a nested
  //    record, deckProperties as plain record) ────────────────────────
  const recordResults: Record<string, unknown> = {};
  for (const entity of recordEntities) {
    const localData = snap[entity.stateKey];
    const result = entity.applySyncDiff(diff, ts, localData);
    if (result !== undefined) recordResults[entity.stateKey] = result;
  }

  // ── Generic entities (from registry) ────────────────────────────────
  const genericsToApply: Record<string, unknown> = {};
  for (const entity of genericEntities) {
    const localData = snap[entity.stateKey];
    const entityDiff = buildEntityDiff(entity.diffKey);
    const result = entity.applySyncDiff(entityDiff, ts, localData);
    if (result !== undefined) genericsToApply[entity.stateKey] = result;
  }

  // ── Flush local-only data to server ──────────────────────────────────
  for (const entity of genericEntities) {
    entity.flushLocal(
      buildEntityDiff(entity.diffKey),
      snap[entity.stateKey],
      ts,
      outbound,
    );
  }
  for (const entity of recordEntities) {
    entity.flushLocal(diff, snap[entity.stateKey], ts, outbound);
  }

  return {
    genericsToApply,
    recordResults,
    outboundMessages: outbound,
    updatedTimestamps: ts,
  };
}

// ---------------------------------------------------------------------------
// detectDirtyChanges
// ---------------------------------------------------------------------------

/**
 * Compare two state snapshots and return outbound messages for dirty entities.
 *
 * `updatedTimestamps` is the FULL map (`curr.serverTimestamps` plus every
 * timestamp this tick advanced) — a fresh object the caller may keep. The
 * per-tick hot path wants only what changed: see {@link detectDirtyChangesDelta}.
 */
export function detectDirtyChanges(
  prev: SyncSnapshot,
  curr: SyncSnapshot,
): {
  messages: SyncClientMessage[];
  updatedTimestamps: Record<string, string>;
} {
  const { messages, updatedTimestamps: delta } = detectDirtyChangesDelta(prev, curr);
  // Same key order as the old in-place copy: existing keys keep their slot,
  // keys first seen this tick are appended in the order they were written.
  return { messages, updatedTimestamps: { ...curr.serverTimestamps, ...delta } };
}

/**
 * {@link detectDirtyChanges} without the copy: `updatedTimestamps` holds ONLY
 * the timestamps this tick advanced (reads fall through to
 * `curr.serverTimestamps`, which is never written). The map has one entry per
 * card in the library, so copying it on every local edit was the dominant
 * per-tick cost on a large library; the delta is the size of the edit.
 *
 * `{ ...curr.serverTimestamps, ...updatedTimestamps }` equals what
 * `detectDirtyChanges` returns (pinned by `sync-engine-delta.spec.ts`). A caller
 * that advances its live map applies it with `Object.assign(live, delta)` —
 * only once the messages actually left the device.
 */
export function detectDirtyChangesDelta(
  prev: SyncSnapshot,
  curr: SyncSnapshot,
): {
  messages: SyncClientMessage[];
  updatedTimestamps: Record<string, string>;
} {
  const messages: SyncClientMessage[] = [];
  const base = curr.serverTimestamps;
  const delta: Record<string, string> = {};

  // Record entities (from registry). Zustand mutations only spread the slice
  // that was actually touched, so an unchanged slice has the same reference
  // in prev and curr. Skipping the whole detectDirty pass in that case turns
  // an O(entries) scan into a single `===` check — by far the dominant win
  // on the card-add path (nothing touches quiz/notes/annotations there).
  for (const entity of recordEntities) {
    const prevSlice = prev[entity.stateKey];
    const currSlice = curr[entity.stateKey];
    if (prevSlice === currSlice) continue;
    entity.detectDirty(prevSlice, currSlice, delta, messages, base);
  }

  // Generic entities (from registry). Same ref-equality short-circuit —
  // folders/globalSrSettings/userInfo are untouched by card mutations, so
  // their slice refs stay equal across prev/curr snapshots.
  for (const entity of genericEntities) {
    const prevSlice = prev[entity.stateKey];
    const currSlice = curr[entity.stateKey];
    if (prevSlice === currSlice) continue;
    entity.detectDirty(prevSlice, currSlice, delta, messages, base);
  }

  return { messages, updatedTimestamps: delta };
}
